import type {ChangeSpec, EditorState} from '@codemirror/state'
import type {List, ListContent, ListItem} from 'mdast'

import {
  buildNextMarkerSyntax,
  buildResetMarkerSyntax,
  findItemPrefixes,
} from '../../../language-tools/markdown/list-utils'
import {
  getChildrenOverlappingRange,
  getNodesContainingRange,
} from '../../../language-tools/syntax-tree/node-range-utils'
import {isPositionedNode, type PositionedNode} from '../../../language-tools/syntax-tree/positioned-node'
import {getMarkdownTree} from '../../extensions/markdown-parser'

/**
 * Create a `ChangeSpec` that would re-create the prefixes for following items in the list to ensure that they are
 * valid with respect to `previousMarker`.
 * @param list The parent list node that contains the items.
 * @param afterIndex Index of the item after which to remark following items. Exclusive - this item won't be changed.
 * @param previousMarker Marker to start building subsequent markers from. Typically, this would be the new marker for
 * the item that will be at `afterIndex`. If `null`, the items will be restarted from 1.
 */
export function remarkFollowingItems(list: List, afterIndex: number, previousMarker: string | null): ChangeSpec {
  const changes: ChangeSpec[] = []
  for (const followingItem of list.children.slice(afterIndex + 1))
    if (followingItem.type === 'listItem') {
      const prefix = findItemPrefixes(followingItem).listItemPrefix
      if (!prefix || !isPositionedNode(prefix)) continue

      const newMarker =
        previousMarker === null ? buildResetMarkerSyntax(prefix.raw) : buildNextMarkerSyntax(previousMarker)

      changes.push({
        // We haven't dispatched the initial change yet and we aren't setting `sequential` to true, so we can keep
        // using the pre-change document positions
        from: prefix.position.start.offset,
        to: prefix.position.end.offset,
        insert: newMarker,
      })
      previousMarker = newMarker
    }
  return changes
}

/** Information about selected list items. */
export interface ListItemsSelection {
  selectedItems: Array<ListItem & PositionedNode>
  list: List
  parents: Array<{
    list: List
    item: ListItem
  }>
}

export function positionedItems(children: ListContent[]) {
  return children.filter((n): n is ListItem & PositionedNode => n.type === 'listItem' && isPositionedNode(n))
}

/**
 * Get the list items that overlap the selection. If multiple items are selected, the `list` returned will be the
 * _lowest common list_ across all the items, and the `selectedItems` will be limited to direct descendants of that
 * list. In other words, we'll ignore sub-items of selected items.
 *
 * If the `list` is a nested sublist, `parents` will contain the parent list items and lists. The last item in this
 * array will be the immediate parent, and the first item will be the top-level ancestor.
 */
export function getListSelection(state: EditorState): ListItemsSelection | null {
  if (state.selection.ranges.length !== 1) return null

  const selectionFrom = state.selection.main.from
  const selectionTo = state.selection.main.to

  const nodesContainingSelection = getNodesContainingRange(getMarkdownTree(state), selectionFrom, selectionTo)

  const listIndex = nodesContainingSelection.findLastIndex(n => n.type === 'list')
  const list = nodesContainingSelection[listIndex] as List | undefined
  if (!list) return null

  const selectedItems = positionedItems(getChildrenOverlappingRange(list, selectionFrom, selectionTo))
  if (selectedItems.length === 0) return null

  const nodesContainingList = nodesContainingSelection.slice(0, listIndex)

  const parents: ListItemsSelection['parents'] = []
  for (const node of nodesContainingList) {
    if (node.type === 'listItem') {
      const last = parents.at(-1)
      if (last) last.item = node
    } else if (node.type === 'list') {
      parents.push({list: node, item: undefined as unknown as ListItem})
    }
  }

  return {list, selectedItems, parents}
}
